import { buildPrimePicksCarouselCategories, getCanonicalLaneDescriptors, type CanonicalPrimePicksCategory, type CanonicalPrimePicksEntry } from '@primetime/shared';
import type { CanonicalAssetGroupDto } from '@primetime/shared/apiContracts';

export interface PrimePicksTaxonomyLane {
  taxonomyKey: string;
  label: string;
  laneKey: string | null;
  laneType: 'genre' | 'subgenre';
  laneGenre: string;
  laneSubgenre: string | null;
}

export interface PrimePicksCarouselSlide {
  key: string;
  laneId: string | null;
  laneType: 'genre' | 'subgenre';
  genre: string;
  subgenre: string | null;
  label: string;
  entries: CanonicalPrimePicksEntry[];
}

function findCanonicalCategory(
  lane: PrimePicksTaxonomyLane,
  categories: CanonicalPrimePicksCategory[]
) {
  if (lane.laneKey) return categories.find((category) => category.laneId === lane.laneKey) || null;
  return categories.find((category) => category.genre === lane.laneGenre && category.subgenre === lane.laneSubgenre) || null;
}

export function buildPrimePicksCarouselSlides(
  assets: CanonicalAssetGroupDto[],
  taxonomyLanes: PrimePicksTaxonomyLane[]
): PrimePicksCarouselSlide[] {
  const canonicalCategories = buildPrimePicksCarouselCategories(assets);
  const representedCanonicalLaneIds = new Set<string>();
  const taxonomySlides = taxonomyLanes.map((lane) => {
    const canonicalCategory = findCanonicalCategory(lane, canonicalCategories);
    if (lane.laneKey) representedCanonicalLaneIds.add(lane.laneKey);
    if (canonicalCategory) representedCanonicalLaneIds.add(canonicalCategory.laneId);

    return {
      key: lane.taxonomyKey,
      laneId: canonicalCategory?.laneId || lane.laneKey,
      laneType: canonicalCategory?.laneType || lane.laneType,
      genre: canonicalCategory?.genre || lane.laneGenre,
      subgenre: canonicalCategory?.subgenre || lane.laneSubgenre,
      label: canonicalCategory?.label || lane.label,
      entries: canonicalCategory?.entries || []
    };
  });

  const unmatchedCanonicalSlides = getCanonicalLaneDescriptors(assets)
    .filter((lane) => !representedCanonicalLaneIds.has(lane.laneId))
    .map((lane) => {
      const category = canonicalCategories.find((candidate) => candidate.laneId === lane.laneId);
      return {
        key: `canonical-${lane.laneId}`,
        laneId: lane.laneId,
        laneType: lane.laneType,
        genre: lane.genre,
        subgenre: lane.subgenre,
        label: lane.subgenre || lane.genre,
        entries: category?.entries || []
      };
    });

  return [...taxonomySlides, ...unmatchedCanonicalSlides];
}
